/**
 * DimensionCauseRecordsDialog - Dialog showing records for a dimension + cause combination
 *
 * Uses the shared RecordsTable component for consistent record display.
 * Clicking a record opens a new tab with the record details.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../../../ui/dialog';
import { Badge } from '../../../../../ui/badge';
import { RecordsTable } from '../molecules';
import { useDimensionCauseRecords } from '../../hooks';
import type { DimensionCauseRecordsDialogProps } from '../../types';
import { CONDUCT_DIMENSIONS } from '../../constants';

export function DimensionCauseRecordsDialog({
  isOpen,
  onClose,
  dimensionId,
  dimensionName,
  causeId,
  causeName,
  datasetId,
  filters,
  onCauseClick,
}: Omit<DimensionCauseRecordsDialogProps, 'onRecordClick'>) {
  const [page, setPage] = useState(1);

  // Reset page when dialog opens or selection changes
  useEffect(() => {
    if (isOpen) {
      setPage(1);
    }
  }, [isOpen, dimensionId, causeId]);

  const { data, isLoading, isError } = useDimensionCauseRecords(
    datasetId,
    dimensionId,
    causeId,
    filters,
    page,
    isOpen && !!dimensionId && !!causeId
  );

  const dimensionColor = dimensionId ? CONDUCT_DIMENSIONS[dimensionId]?.color : undefined;

  const handleRecordClick = (recordId: string) => {
    window.open(`/v4/records/${datasetId}/${recordId}`, '_blank');
  };

  const handlePreviousPage = () => {
    setPage((p) => Math.max(1, p - 1));
  };

  const handleNextPage = () => {
    if (data && page < data.totalPages) {
      setPage((p) => p + 1);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[90vw] sm:w-[1200px] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {dimensionColor && (
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: dimensionColor }}
              />
            )}
            {dimensionName} + {causeName} Records
            {data && (
              <Badge variant="secondary" className="ml-2">
                {data.totalRecords} total
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Records associated with this dimension and cause combination
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[340px] overflow-y-auto overflow-x-hidden">
          {isError ? (
            <div className="py-8 text-center text-red-500">
              Failed to load records. Please try again.
            </div>
          ) : (
            <RecordsTable
              records={data?.records ?? []}
              onRecordClick={handleRecordClick}
              onCauseClick={onCauseClick}
              isLoading={isLoading}
              emptyMessage="No records found for this dimension and cause combination."
              loadingRows={5}
              stickyHeader
              actionIcon="external"
              pagination={
                data && data.totalPages > 1
                  ? {
                      page: data.page,
                      totalPages: data.totalPages,
                      totalRecords: data.totalRecords,
                      onPreviousPage: handlePreviousPage,
                      onNextPage: handleNextPage,
                      isLoading,
                    }
                  : undefined
              }
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
